import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { Logo } from "./ui";

const drawerFocusableSelector = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])';

export function wrapDrawerFocus(container: HTMLElement, shiftKey: boolean): boolean {
  const focusable = [...container.querySelectorAll<HTMLElement>("*")]
    .filter((element) => element.matches(drawerFocusableSelector));
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (first === undefined || last === undefined) return false;
  const activeElement = document.activeElement;
  if (shiftKey && activeElement !== null && first.isSameNode(activeElement)) {
    last.focus();
    return true;
  }
  if (!shiftKey && activeElement !== null && last.isSameNode(activeElement)) {
    first.focus();
    return true;
  }
  return false;
}

export function AppNavigationDrawer({ open, onClose, openerRef, routeKey, children, footer }: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly openerRef: RefObject<HTMLButtonElement>;
  readonly routeKey: string;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousRouteRef = useRef(routeKey);

  useEffect(() => {
    if (previousRouteRef.current !== routeKey && open) onClose();
    previousRouteRef.current = routeKey;
  }, [onClose, open, routeKey]);

  useEffect(() => {
    if (drawerRef.current !== null) drawerRef.current.inert = !open;
    if (!open) return;
    const focusReturnTarget = openerRef.current;
    const focusClose = () => closeRef.current?.focus();
    const frame = typeof window.requestAnimationFrame === "function" ? window.requestAnimationFrame(focusClose) : window.setTimeout(focusClose, 0);
    const handleDrawerKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      if (drawerRef.current === null) {
        event.preventDefault();
        closeRef.current?.focus();
        return;
      }
      if (wrapDrawerFocus(drawerRef.current, event.shiftKey)) event.preventDefault();
    };
    window.addEventListener("keydown", handleDrawerKey);
    return () => {
      if (typeof window.cancelAnimationFrame === "function") window.cancelAnimationFrame(frame);
      else window.clearTimeout(frame);
      window.removeEventListener("keydown", handleDrawerKey);
      focusReturnTarget?.focus();
    };
  }, [onClose, open, openerRef]);

  return (
    <>
      {open && <button type="button" aria-label="Close navigation" onClick={onClose} className="fixed inset-0 z-[70] bg-slate-900/30 backdrop-blur-[1px]" />}
      <div
        ref={drawerRef}
        id="app-navigation-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Application navigation"
        aria-hidden={!open}
        className={`fixed inset-y-0 left-0 z-[80] flex w-[min(88vw,330px)] flex-col border-r border-[var(--shell-line)] bg-[var(--shell)] text-[var(--shell-text)] shadow-2xl transition-transform duration-200 ${open ? "translate-x-0" : "invisible -translate-x-full"}`}
      >
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-[var(--shell-line)] px-5">
          <Logo size={18} />
          <button ref={closeRef} type="button" aria-label="Close navigation" onClick={onClose} className="grid h-10 w-10 place-items-center rounded text-xl text-slate-500 hover:bg-white hover:text-slate-900">×</button>
        </div>
        <nav aria-label="Main navigation" className="flex-1 overflow-y-auto px-3 py-4">{children}</nav>
        {footer !== undefined && <div className="border-t border-[var(--shell-line)] bg-[var(--shell-raised)] px-4 py-4">{footer}</div>}
      </div>
    </>
  );
}
